import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { toast } from "react-toastify";

import {
  getAccountDisplayName,
  getAccountPlatformType,
} from "@/entities/accounts";
import { replaceAccount } from "@/entities/accounts/table";
import { AccountType } from "shared/types/accounts";
import { reconnectFacebookAccount } from "api/social-account-connect/facebook";
import {
  getUserFriendlyErrorMessage,
  UserFriendlyError,
} from "api/UserFriendlyError";
import { getPinterestRedirect } from "api/social-account-connect/pinterest";
import { getGmbRedirect } from "api/social-account-connect/google";
import { getYtRedirect } from "api/social-account-connect/youtube";
import { getTwitterRedirect } from "api/social-account-connect/twitter";
import { getLinkedInRedirect } from "api/social-account-connect/linkedin";
import { getTikTokRedirect } from "api/social-account-connect/tiktok";
import { ONLY_MANUAL_PLATFORMS } from "constants/Platforms";
import { reconnectManualAccount } from "api/accounts";
import { PlatformNameToPlatformTypeMap } from "features/post/format-post";
import { redirectToThreadsAuthPage } from "../../../api/social-account-connect/threads/trigger-auth-redirect";
import { ReduxRootState } from "@/types/redux-store";
import {
  FeatureName,
  localStorageFeatureToggle,
} from "@/utils/feature-toggle-utils";

export interface ReconnectAccountProps {
  account: AccountType;
  isPausedAccount?: boolean;
  otherAccountsDisabled?: boolean;
}

export type ReconnectAccountState = {
  pending: boolean;
};

const reconnectMethods: Record<
  string,
  (acc: AccountType) => Promise<AccountType | null>
> = {
  FB: reconnectFacebookAccount,
  PI: async (_: AccountType) => {
    window.location.href = await getPinterestRedirect(true);
    return null;
  },
  GMB: async (_: AccountType) => {
    window.location.href = await getGmbRedirect(true);
    return null;
  },
  YT: async (_: AccountType) => {
    window.location.href = await getYtRedirect(true);
    return null;
  },
  TW: async (account: AccountType) => {
    window.location.href = await getTwitterRedirect(account.login, true);
    return null;
  },
  TT: async (account: AccountType) => {
    window.location.href = await getTikTokRedirect(
      true,
      account.login,
      account._id,
      localStorageFeatureToggle.get(FeatureName.TiktokDMScope)
    );
    return null;
  },
  TH: async (account: AccountType) => {
    window.location.href = await redirectToThreadsAuthPage({
      isReconnect: true,
      skedAccountId: account._id,
    });
    return null;
  },
  LI: async (_: AccountType) => {
    window.location.href = await getLinkedInRedirect(true);
    return null;
  },
};

const featureKey = "reconnectAccount" as const;

const initialState: ReconnectAccountState = {
  pending: false,
};

function getFeatureState(state: ReduxRootState): ReconnectAccountState {
  return (
    (
      state as ReduxRootState & {
        [featureKey]?: ReconnectAccountState;
      }
    )[featureKey] ?? initialState
  );
}

export function isReconnectSupported(account: AccountType): boolean {
  return Boolean(reconnectMethods[getAccountPlatformType(account)]);
}

export const reconnectAccountFx = createAsyncThunk(
  "reconnectAccount/run",
  async (
    { account, isPausedAccount, otherAccountsDisabled }: ReconnectAccountProps,
    thunkApi
  ) => {
    try {
      const accountType = getAccountPlatformType(account);
      const isTTAccount = accountType === PlatformNameToPlatformTypeMap.tiktok;
      const isAutoPostTT = isTTAccount && account.platformId;

      ONLY_MANUAL_PLATFORMS.forEach((platformType) => {
        reconnectMethods[platformType] = reconnectManualAccount;
      });

      let method = reconnectMethods[accountType];

      if (
        (isPausedAccount && !isAutoPostTT) ||
        (otherAccountsDisabled && isTTAccount && !account.platformId)
      ) {
        method = reconnectManualAccount;
      }

      if (!method) {
        throw new UserFriendlyError(
          `Reconnecting is not supported for ${getAccountPlatformType(
            account
          )} accounts`
        );
      }

      const updatedAccount = await method(account);

      if (updatedAccount) {
        thunkApi.dispatch(replaceAccount(updatedAccount));
        toast.success(
          `Great! You have successfully reconnected ${getAccountDisplayName(
            updatedAccount
          )}. You might need to refresh the page to see any changes.`
        );
      }

      return updatedAccount;
    } catch (error) {
      toast.error(getUserFriendlyErrorMessage(error));
      throw error;
    }
  }
);

const reconnectAccountSlice = createSlice({
  name: featureKey,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(reconnectAccountFx.pending, (state) => {
      state.pending = true;
    });
    builder.addCase(reconnectAccountFx.fulfilled, (state) => {
      state.pending = false;
    });
    builder.addCase(reconnectAccountFx.rejected, (state) => {
      state.pending = false;
    });
  },
});

export const reconnectAccountReducer = reconnectAccountSlice.reducer;

export const selectReconnectAccountPending = createSelector(
  getFeatureState,
  (reconnectAccountState) => reconnectAccountState.pending
);
